import React, { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { ErrorBoundary } from "react-error-boundary";

import { getEnvConfig } from 'kmstack-shared/utils';
import { initApiClient } from 'kmstack-shared/services';
import { setupSpinner } from 'kmstack-shared/components';
import { InactivityProvider } from 'kmstack-shared/providers';
import { useKeyboardShortcuts } from 'kmstack-shared/hooks';

import AppRouter from './routes/AppRouter';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { I18nProvider } from './context/I18nProvider';
import { GlobalErrorFallback } from './errors/GlobalErrorFallback';
import { cn } from './common/ui/Core';
import { ActionBanner } from './common/ui/ActionBanner';
import { ShortcutsHelpModal } from './components/modals/ShortcutsHelpModal';
import { AuthService } from './routes/auth.service';
import { store } from './store';

const isProd = import.meta.env.PROD

function ApiClientInitializer({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const envConfig = getEnvConfig()
        initApiClient({
            mode: isProd ? 'prod' : 'local',
            apiUrl: envConfig.apiUrl,
            loginUrl: `${ envConfig.accountUrl }/login`,
            authPages: ['/login', '/signup', '/reset-password'],
            allowedHosts: envConfig.allowedHosts,
            redirectOnLogout: false,
            dispatch
        });
        setupSpinner(cn)
        setIsInitialized(true);
    }, [dispatch]);

    return <>{isInitialized ? children : null}</>;
}


const App: React.FC = () => {

    const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);
    const [showInactivityWarning, setShowInactivityWarning] = useState(false);

    useKeyboardShortcuts([
        {
            combo: { key: '/', ctrlKey: true },
            handler: () => setIsShortcutsHelpOpen(true),
            description: 'Open shortcuts help',
        },
        {
            combo: { key: '/', metaKey: true }, // Cmd+/ for Mac
            handler: () => setIsShortcutsHelpOpen(true),
            description: 'Open shortcuts help',
        }
    ]);

    const shortcutsList = [
        { keys: ['Ctrl', '/'], description: 'Open shortcuts help' },
        { keys: ['⌘', '/'], description: 'Open shortcuts help (Mac)' },
        { keys: ['Esc'], description: 'Close modals / menus' },
        { keys: ['Tab'], description: 'Navigate forward' },
        { keys: ['Shift', 'Tab'], description: 'Navigate backward' },
        { keys: ['Space'], description: 'Activate buttons' },
        { keys: ['Enter'], description: 'Submit forms / Activate links' },
    ];

    const handleInactivityWarning = () => {
        setShowInactivityWarning(true);
        // User requested 55 seconds
        setTimeout(() => setShowInactivityWarning(false), 55000);
    };

    return (
        <ApiClientInitializer>
            <InactivityProvider
                timeout={30}
                warningTimeout={1}
                onTimeout={() => {
                    AuthService.logout(store.dispatch)
                }}
                onWarning={handleInactivityWarning}
            >
                {/* Skip navigation link for accessibility */}
                {/* <a href="#main-content" className="skip-link">
                    Skip to main content
                </a> */}

                {showInactivityWarning && (
                    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] w-full max-w-md px-4 pointer-events-none">
                        <div className="pointer-events-auto shadow-2xl">
                            <ActionBanner
                                type="warning"
                                title="Session Expiring"
                                message="You have been inactive for a while. You will be logged out in less than a minute."
                                actionLabel="I'm here"
                                onAction={() => window.location.reload()} // Simple activity trigger
                                onDismiss={() => setShowInactivityWarning(false)}
                            />
                        </div>
                    </div>
                )}

                <ShortcutsHelpModal
                    isOpen={isShortcutsHelpOpen}
                    onClose={() => setIsShortcutsHelpOpen(false)}
                    shortcuts={shortcutsList}
                />
                <ErrorBoundary
                    FallbackComponent={GlobalErrorFallback}
                    onError={(error, info) => {
                        // Optional: send to Sentry, LogRocket, etc.
                        console.error("Global Error:", error, info);
                    }}
                >
                    <FeatureFlagProvider>
                        <I18nProvider>
                            {/* this is has notification the same as AlertProvider */}
                            <AppRouter />
                        </I18nProvider>
                    </FeatureFlagProvider>
                </ErrorBoundary>
            </InactivityProvider>
        </ApiClientInitializer>
    )
};

export default App;
